<script setup lang="ts">
import { ref, computed } from 'vue';
import { transactionParamsSwitch } from '@/common/sb-transaction.helpers';
import { TRANSACTION_TYPE_NAME_ENUM, getTransactionId } from '@/common/transaction-utils';
import PlsButton from '@/components/PlsButton.vue';
import TransactionItem from './TransactionItem.vue';
import type { FullTransaction, TransactionSelectEvent, TransactionSubmitEvent } from './types';
import type { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { TransactionType } from '@/common/config';
import { useTransactionListStyles } from './composables/useTransactionListStyles';
import { tConversation } from '@/plugins/i18n';

export interface TransactionListProps {
  transactions: FullTransaction[];
  transactionSubtypes: ExternalTransactionKind[];
  editable?: boolean;
  multiSelect?: boolean;
  showLoadMore?: boolean;
  loading?: boolean;
  isSubmitting?: boolean;
  submitButtonText?: string;
  canSubmit?: boolean;
  error?: string | null;
  /** Initial selected transactions (for mocked flows) */
  initialSelectedTransactions?: FullTransaction[];
  /** If the transaction selection is cancelable */
  cancelable?: boolean;
}

const props = withDefaults(defineProps<TransactionListProps>(), {
  editable: true,
  multiSelect: true,
  showLoadMore: false,
  loading: false,
  isSubmitting: false,
  submitButtonText: 'Select',
  canSubmit: true,
  initialSelectedTransactions: () => [],
  cancelable: false,
});

const emit = defineEmits<{
  (e: 'select', event: TransactionSelectEvent): void;
  (e: 'submit', event: TransactionSubmitEvent): void;
  (e: 'load-more'): void;
  (e: 'cancel'): void;
}>();

// Initialize with pre-selected transactions if provided
const selectedTransactions = ref<FullTransaction[]>([...props.initialSelectedTransactions]);

const isTransactionSelected = (transaction: FullTransaction): boolean => {
  const txId = getTransactionId(transaction);
  return selectedTransactions.value.some((t) => getTransactionId(t) === txId);
};

const handleToggle = (transaction: FullTransaction) => {
  const isSelected = isTransactionSelected(transaction);

  if (isSelected) {
    const txId = getTransactionId(transaction);
    selectedTransactions.value = selectedTransactions.value.filter((t) => getTransactionId(t) !== txId);
  } else {
    if (props.multiSelect) {
      selectedTransactions.value = [...selectedTransactions.value, transaction];
    } else {
      selectedTransactions.value = [transaction];
    }
  }

  emit('select', {
    transaction,
    selected: !isSelected,
  });
};

const handleSubmit = () => {
  emit('submit', {
    transactions: selectedTransactions.value,
  });
};

const handleCancel = () => {
  emit('cancel');
};

const handleLoadMore = () => {
  emit('load-more');
};

const internalCanSubmit = computed(() => {
  return (selectedTransactions.value.length > 0 || isNotListedSelected.value) && !props.loading && !props.isSubmitting;
});

const canSubmit = computed(() => {
  // Use external canSubmit prop if provided, otherwise use internal logic
  const propsValidation = props.canSubmit !== undefined ? props.canSubmit : true;
  return propsValidation && internalCanSubmit.value;
});

// Always show "Transaction not listed" option
const showNotListedOption = computed(() => {
  return !props.loading;
});

const isNotListedSelected = ref(false);

const handleNotListedToggle = () => {
  if (!props.editable) {
    return;
  }

  // Toggle the selection state
  isNotListedSelected.value = !isNotListedSelected.value;

  // Clear other selections when selecting "not listed"
  if (isNotListedSelected.value) {
    selectedTransactions.value = [];
  }
};

const handleTransactionToggle = (transaction: FullTransaction) => {
  // Clear "not listed" selection when selecting a transaction
  isNotListedSelected.value = false;
  handleToggle(transaction);
};

// Compute transaction type name and title from subtypes
const firstSubtype = computed(() => props.transactionSubtypes[0]);

const transactionParams = computed(() => {
  if (!firstSubtype.value) return null;
  if (firstSubtype.value.startsWith('BETS_')) {
    return { type: TransactionType.Bet };
  }

  return transactionParamsSwitch.match(firstSubtype.value);
});

const transactionType = computed(() => {
  const type = transactionParams.value?.type as TransactionType;
  return type;
});

const transactionTypeName = computed(() => {
  const type = transactionParams.value?.type;
  if (type === undefined) return '';
  return `${TRANSACTION_TYPE_NAME_ENUM[type as keyof typeof TRANSACTION_TYPE_NAME_ENUM]}s`;
});

const displayTitle = computed(() => {
  const typeName = transactionTypeName.value;

  if (props.transactionSubtypes.length > 0) {
    // Extract status labels from all subtypes
    // e.g., ["DEPOSITS_PENDING", "DEPOSITS_FAILED"] -> "Pending, Failed Deposits"
    const statusLabels = props.transactionSubtypes
      .map((subtype) => {
        const parts = subtype.split('_');
        if (parts.length > 1) {
          const statusPart = parts.slice(1).join(' ').toLowerCase();
          return statusPart.charAt(0).toUpperCase() + statusPart.slice(1);
        }
        return '';
      })
      .filter((label) => label !== '');

    if (statusLabels.length > 0) {
      return `${statusLabels.join(', ')} ${typeName}`;
    }
  }

  return typeName;
});

// Styles and customization
const {
  loadingCardStyle,
  loadingTextStyle,
  errorCardStyle,
  errorTextStyle,
  errorDetailsStyle,
  emptyListStyle,
  emptyListText,
  emptyTextStyle,
  selectButtonStyles,
  selectButtonDisabledStyles,
  loadMoreButtonStyles,
  cancelButtonStyles,
  cancelButtonText,
  getSelectButtonText,
  getLoadMoreButtonText,
  getNotListedContainerStyle,
  getNotListedRadioStyle,
  notListedRadioDotStyle,
  notListedTextStyle,
} = useTransactionListStyles();

// Computed values using factory functions for props that change
const selectButtonText = computed(() => getSelectButtonText(props.isSubmitting, props.submitButtonText));
const loadMoreButtonText = computed(() => getLoadMoreButtonText(props.loading));

// Computed "not listed" option styles using factory functions
const notListedContainerStyle = computed(() => getNotListedContainerStyle(isNotListedSelected.value));
const notListedRadioStyle = computed(() => getNotListedRadioStyle(isNotListedSelected.value));

const notListedText = computed(() => tConversation('page.issue.none_of_these'));
</script>

<template>
  <div class="transaction-list">
    <div v-if="displayTitle" class="theme-font-main transaction-list__title">
      {{ displayTitle }}
    </div>

    <!-- Error State -->
    <div v-if="error" class="card-radius transaction-list__error" :style="errorCardStyle">
      <p class="theme-font-main" :style="errorTextStyle">Failed to load transactions. Please try again.</p>
      <p class="error-details theme-font-subtext" :style="errorDetailsStyle">{{ error }}</p>
    </div>

    <!-- Loading State -->
    <div
      v-else-if="loading && transactions.length === 0"
      class="card-radius transaction-list__loading"
      :style="loadingCardStyle"
    >
      <p class="theme-font-main" :style="loadingTextStyle">Loading transactions...</p>
    </div>

    <div v-else class="transaction-list__items">
      <slot
        v-for="transaction in transactions"
        name="transaction-item"
        :transaction="transaction"
        :is-selected="isTransactionSelected(transaction)"
        :on-toggle="() => handleTransactionToggle(transaction)"
      >
        <!-- Default slot content -->
        <TransactionItem
          :key="getTransactionId(transaction)"
          :transaction="transaction"
          :transaction-type="transactionType"
          :editable="editable"
          :is-selected="isTransactionSelected(transaction)"
          @toggle="handleTransactionToggle"
        />
      </slot>

      <div
        v-if="transactions.length === 0 && !loading"
        class="card-radius transaction-list__empty"
        :style="emptyListStyle"
      >
        <span class="theme-font-main" :style="emptyTextStyle">{{ emptyListText }}</span>
      </div>

      <!-- Transaction not listed option -->
      <div
        v-if="showNotListedOption"
        class="card-radius transaction-list__not-listed"
        :class="{ 'is-selected': isNotListedSelected }"
        :style="notListedContainerStyle"
        @click="handleNotListedToggle"
      >
        <div
          class="transaction-list__not-listed-radio"
          :class="{ 'is-selected': isNotListedSelected }"
          :style="notListedRadioStyle"
        >
          <div
            v-if="isNotListedSelected"
            class="transaction-list__not-listed-radio-dot"
            :style="notListedRadioDotStyle"
          ></div>
        </div>
        <span class="theme-font-main transaction-list__not-listed-text" :style="notListedTextStyle">{{
          notListedText
        }}</span>
      </div>
    </div>

    <div v-if="editable" class="transaction-list__actions">
      <!-- Show load more in place of cancel button when not cancelable -->
      <PlsButton
        v-if="!cancelable && showLoadMore"
        :disabled="loading"
        :styles="loadMoreButtonStyles"
        @click="handleLoadMore"
      >
        {{ loadMoreButtonText }}
      </PlsButton>

      <PlsButton v-if="cancelable" :styles="cancelButtonStyles" @click="handleCancel">
        {{ cancelButtonText }}
      </PlsButton>

      <PlsButton
        data-testid="transaction-list-submit"
        :disabled="!canSubmit"
        :styles="selectButtonStyles"
        :disabled-styles="selectButtonDisabledStyles"
        @click="handleSubmit"
      >
        {{ selectButtonText }}
      </PlsButton>
    </div>

    <!-- Show load more centered below actions when cancelable -->
    <div v-if="editable && cancelable && showLoadMore" class="transaction-list__load-more">
      <PlsButton :disabled="loading" :styles="loadMoreButtonStyles" @click="handleLoadMore">
        {{ loadMoreButtonText }}
      </PlsButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.transaction-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.transaction-list__actions {
  display: flex;
  gap: 8px;
}

.transaction-list__load-more {
  display: flex;
  justify-content: center;
}

.transaction-list__items {
  display: flex;
  flex-direction: column;
}

.transaction-list__not-listed {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
}

.transaction-list__empty + .transaction-list__not-listed {
  margin-top: 12px;
}

.transaction-list__not-listed-radio {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.transaction-list__not-listed-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.transaction-list__empty {
  padding: 16px;
  border: 1px solid;
  text-align: center;
}

.transaction-list__error {
  padding: 16px;
  border: 1px solid;

  p {
    margin: 0;

    &:first-child {
      margin-bottom: 4px;
    }
  }

  .error-details {
    margin-top: 8px;
  }
}

.transaction-list__loading {
  padding: 16px;
  border: 1px solid;
  text-align: center;

  p {
    margin: 0;
  }
}
</style>
